import { useCallback, useMemo } from "react";
import { useQueries } from "react-query";
import { UserOrEmailMapping } from "../../../features/Admin/typings/admin.types";
import { UserRole } from "../../../features/Login/typings/auth.types";
import { getLocAdmins } from "../../../utils/axios-requests";
import { getUserIdsOfUserOrEmailMapping } from "../../../utils/type/type.utils";

export function useRequestAdminLocationAccess(params: {
  locationInventoryIds: number[];
  userId: string | undefined;
  isRequestAdminRealm: boolean;
}): {
  canManageRequestedAtLocation: (locationInventoryId: number | null | undefined) => boolean;
  isLookupPending: boolean;
} {
  const { locationInventoryIds, userId, isRequestAdminRealm } = params;

  const uniqueIds = useMemo(
    () => [...new Set(locationInventoryIds.filter(id => id > 0))],
    [locationInventoryIds]
  );

  const queries = useQueries(
    uniqueIds.map(locationInventoryId => ({
      queryKey: ["loc-request-admins", locationInventoryId, userId],
      queryFn: async (): Promise<boolean> => {
        const res = await getLocAdmins(locationInventoryId, UserRole.isRequestAdmin, undefined);
        const admins = (res.data ?? []) as UserOrEmailMapping[];
        const ids = getUserIdsOfUserOrEmailMapping(admins);
        return ids.includes(userId as string);
      },
      enabled:
        isRequestAdminRealm && userId != null && userId.length > 0 && locationInventoryId > 0,
      staleTime: 5 * 60 * 1000,
      retry: 1
    }))
  );

  const allowedLocationIds = useMemo(() => {
    const allowed = new Set<number>();
    uniqueIds.forEach((id, index) => {
      if (queries[index]?.data === true) {
        allowed.add(id);
      }
    });
    return allowed;
  }, [uniqueIds, queries]);

  const isLookupPending =
    isRequestAdminRealm &&
    uniqueIds.length > 0 &&
    queries.some(query => query.isLoading || query.isFetching);

  const canManageRequestedAtLocation = useCallback(
    (locationInventoryId: number | null | undefined): boolean => {
      if (!isRequestAdminRealm) {
        return false;
      }
      if (locationInventoryId == null || locationInventoryId <= 0) {
        return false;
      }
      if (isLookupPending) {
        return false;
      }
      return allowedLocationIds.has(locationInventoryId);
    },
    [allowedLocationIds, isLookupPending, isRequestAdminRealm]
  );

  return { canManageRequestedAtLocation, isLookupPending };
}
